"use client";

import { useEffect, useRef } from "react";
import { Controller, type Control } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { Switch } from "@/components/shadcn/switch";
import {
  ComplexMarkdownField,
  ComplexMarkdownEditor,
} from "@/components/Form/ComplexMarkdownField";
import { MilkdownProvider } from "@milkdown/react";
import { ToggleField } from "@/components/Form/ToggleField";
import {
  NumberInput,
  NumberInputField,
} from "@/components/Form/NumberInputField";
import { CodeEditorSurface } from "@/components/Form/CodeEditor/CodeEditor";
import { DatePickerInput } from "@/components/Form/DatePickerField";
import { FileUpload, FileUploadField } from "@/components/Form/FileInputField";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { toSpotFiles } from "@/components/Form/spotFiles";
import { fileTypesForDataType } from "@/utils/mimetypes";
import { cn } from "@/lib/utils";

type FormValues = Record<string, unknown>;

const STRUCTURED_INPUT_TYPES = ["object", "list", "dict", "basemodel"];
const FILE_INPUT_TYPES = ["File", "Image", "Audio"];

// "logo" is the large drop zone used by run forms; "inline" is the compact
// single-line picker that matches the other file fields in node configuration.
export type TypedValueFileVariant = "inline" | "logo";

export function isStructuredInputType(dataType: string): boolean {
  return STRUCTURED_INPUT_TYPES.includes(dataType);
}

export function isFileInputType(dataType: string): boolean {
  return FILE_INPUT_TYPES.includes(dataType);
}

// "false" saved as text must not read as on.
export function isTrueValue(value: unknown): boolean {
  return value === true || value === "true";
}

export function emptyScalarValue(dataType: string): unknown {
  if (isStructuredInputType(dataType)) return "{}";
  if (isFileInputType(dataType)) return [];
  if (dataType === "bool") return false;
  return "";
}

export function emptyTypedValue(dataType: string, isList: boolean): unknown {
  return isList ? [] : emptyScalarValue(dataType);
}

function asJsonText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return JSON.stringify(value, null, 2);
}

// Object values are authored as JSON. The CodeMirror surface underlines
// invalid JSON as you type (its json linter), so a typo is visible before the
// run is submitted; the value itself stays text until submit parses it.
function JsonValueEditor({
  value,
  onChange,
  onBlur,
  isDisabled,
  label,
  placeholder,
  minRows,
  autoFocus,
}: {
  value: unknown;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  minRows?: number;
  autoFocus?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {label && <NoxusFormLabel label={label} noMargin />}
      <CodeEditorSurface
        value={asJsonText(value)}
        onChange={onChange}
        onBlur={onBlur}
        isDisabled={isDisabled}
        inputs={[]}
        language="json"
        minRows={minRows ?? 4}
        placeholder={placeholder ?? "{}"}
        autoFocus={autoFocus}
      />
    </div>
  );
}

interface ScalarValueFieldProps {
  control: Control<FormValues>;
  name: string;
  label: string;
  dataType: string;
  isDisabled?: boolean;
  fileVariant?: TypedValueFileVariant;
  disableVariables?: boolean;
}

export function ScalarValueField({
  control,
  name,
  label,
  dataType,
  isDisabled,
  fileVariant = "logo",
  disableVariables,
}: ScalarValueFieldProps) {
  if (dataType === "bool") {
    return (
      <ToggleField
        control={control}
        name={name}
        label={label}
        isDisabled={isDisabled}
      />
    );
  }

  if (dataType === "number") {
    return (
      <NumberInputField
        control={control}
        name={name}
        label={label}
        step="any"
        isDisabled={isDisabled}
      />
    );
  }

  if (isStructuredInputType(dataType)) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <JsonValueEditor
            label={label}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            isDisabled={isDisabled}
          />
        )}
      />
    );
  }

  if (isFileInputType(dataType)) {
    if (fileVariant === "inline") {
      return (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <ScalarValueInput
              dataType={dataType}
              value={field.value}
              onChange={field.onChange}
              isDisabled={isDisabled}
              label={label}
              fileVariant="inline"
            />
          )}
        />
      );
    }
    return (
      <FileUploadField
        control={control}
        name={name}
        label={label}
        canSelectMultipleFiles={false}
        variant="logo"
        isDisabled={isDisabled}
        allowed_file_types={fileTypesForDataType(dataType)}
      />
    );
  }

  if (dataType === "datetime") {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DatePickerInput
            label={label}
            value={typeof field.value === "string" ? field.value : ""}
            onChange={field.onChange}
            onClear={() => field.onChange("")}
            onBlur={field.onBlur}
            isDisabled={isDisabled}
            includeTime
          />
        )}
      />
    );
  }

  return (
    <ComplexMarkdownField
      control={control}
      name={name}
      label={label}
      placeholder={
        label ? `Enter ${label.toLowerCase()}...` : "Enter a value..."
      }
      inputs={[]}
      isDisabled={isDisabled}
      disableVariables={disableVariables}
    />
  );
}

interface TypedValueFieldProps {
  control: Control<FormValues>;
  name: string;
  label: string;
  dataType: string;
  isList: boolean;
  isDisabled?: boolean;
  // When provided, the value is reset to the empty default whenever the element
  // type or list-ness changes — a value entered for one type (e.g. a list of
  // strings) is meaningless for another and must not be re-read by the new
  // editor. Passed by the InputNode's "Fixed value" field, whose `type` can
  // change; the run form omits it (a run input's type is fixed).
  setValue?: (name: string, value: unknown) => void;
  fileVariant?: TypedValueFileVariant;
  // The Input node's fixed value is a constant: its text editors take no
  // variables, so `/` is a plain slash there. Run forms leave this unset.
  disableVariables?: boolean;
}

interface TypedValueInputProps {
  dataType: string;
  isList: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
  fileVariant?: TypedValueFileVariant;
  disableVariables?: boolean;
}

interface ControlledScalarRowProps {
  value: unknown;
  onChange: (value: unknown) => void;
  dataType: string;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  disableVariables?: boolean;
}

/**
 * A single, fully-controlled list-item editor (value/onChange). List rows are
 * controlled — not registered to react-hook-form per index — because RHF's
 * useFieldArray doesn't reliably track arrays of primitives (values revert on
 * blur, append gets flaky). The parent owns the whole array via one Controller.
 */
function ControlledScalarRow({
  value,
  onChange,
  dataType,
  isDisabled,
  autoFocus,
  placeholder,
  disableVariables,
}: ControlledScalarRowProps) {
  if (dataType === "bool") {
    const checked = isTrueValue(value);
    return (
      <div className="flex  items-center gap-2 rounded-md border border-input px-3 py-1.5">
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          disabled={isDisabled}
          autoFocus={autoFocus}
        />
        <span className="text-sm text-muted-foreground">
          {checked ? "True" : "False"}
        </span>
      </div>
    );
  }

  if (dataType === "number") {
    const numeric =
      typeof value === "number" ? value : Number.parseFloat(String(value));
    return (
      <NumberInput
        step="any"
        value={Number.isFinite(numeric) ? numeric : undefined}
        isDisabled={isDisabled}
        onChange={onChange}
        onClear={() => onChange("")}
        onEmptied={() => onChange("")}
        autoFocus={autoFocus}
        placeholder={placeholder}
      />
    );
  }

  if (dataType === "datetime") {
    return (
      <DatePickerInput
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        onClear={() => onChange("")}
        isDisabled={isDisabled}
        placeholder={placeholder}
        includeTime
      />
    );
  }

  if (isStructuredInputType(dataType)) {
    return (
      <JsonValueEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        minRows={2}
        autoFocus={autoFocus}
      />
    );
  }

  return (
    <MilkdownProvider>
      <ComplexMarkdownEditor
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        inputs={[]}
        isDisabled={isDisabled}
        hideToolbar
        compact
        minRows={1}
        maxRows={5}
        placeholder={placeholder ?? "Enter a value..."}
        autoFocus={autoFocus}
        disableVariables={disableVariables}
      />
    </MilkdownProvider>
  );
}

function ScalarValueInput({
  dataType,
  value,
  onChange,
  isDisabled,
  label,
  placeholder,
  fileVariant = "logo",
  disableVariables,
}: Omit<TypedValueInputProps, "isList">) {
  if (isFileInputType(dataType)) {
    return (
      <div className="flex flex-col gap-2">
        {label && <NoxusFormLabel label={label} noMargin />}
        <FileUpload
          values={toSpotFiles(value)}
          setValues={(files) => onChange(files)}
          canSelectMultipleFiles={false}
          showSelectedFiles
          isDisabled={isDisabled}
          allowed_file_types={fileTypesForDataType(dataType)}
          variant={fileVariant}
        />
      </div>
    );
  }

  if (isStructuredInputType(dataType)) {
    return (
      <JsonValueEditor
        label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {label && <NoxusFormLabel label={label} noMargin />}
      <ControlledScalarRow
        value={value}
        onChange={onChange}
        dataType={dataType}
        isDisabled={isDisabled}
        placeholder={placeholder}
        disableVariables={disableVariables}
      />
    </div>
  );
}

function ListValueInput({
  dataType,
  value,
  onChange,
  isDisabled,
  label,
  fileVariant = "logo",
  disableVariables,
}: Omit<TypedValueInputProps, "isList" | "placeholder">) {
  // Stable per-row keys that survive add/remove at any position. The value
  // stays a plain array (no `{id, value}` wrapper to strip on submit); ids are
  // tracked in a parallel ref. Keying rows by index would shift every key on a
  // mid-list removal and remount the wrong rows — which loses in-progress state
  // in the Milkdown text editor each row can host.
  const keysRef = useRef<number[]>([]);
  const nextKeyRef = useRef(0);
  // Key of the row created by "Add item", so it mounts focused. Keys are never
  // reused, so a stale value (row since removed, keys resynced) just no-ops.
  const pendingFocusKeyRef = useRef<number | null>(null);

  const items: unknown[] = Array.isArray(value) ? value : [];

  // Resync keys to the item count on any *external* change (initial load,
  // clear-on-type-change). Internal add/remove keep them aligned in their
  // handlers, so this no-ops for those and the surviving rows keep keys.
  if (keysRef.current.length !== items.length) {
    keysRef.current = items.map(() => nextKeyRef.current++);
  }
  const keys = keysRef.current;

  if (isFileInputType(dataType)) {
    return (
      <div className="flex flex-col gap-2">
        {label && <NoxusFormLabel label={label} noMargin />}
        <FileUpload
          values={toSpotFiles(items)}
          setValues={(files) => onChange(files)}
          canSelectMultipleFiles
          showSelectedFiles
          isDisabled={isDisabled}
          allowed_file_types={fileTypesForDataType(dataType)}
          variant={fileVariant}
        />
      </div>
    );
  }

  const setItem = (index: number, next: unknown) =>
    onChange(items.map((item, i) => (i === index ? next : item)));
  const removeItem = (index: number) => {
    keysRef.current = keysRef.current.filter((_, i) => i !== index);
    onChange(items.filter((_, i) => i !== index));
  };
  const addItem = () => {
    const key = nextKeyRef.current++;
    pendingFocusKeyRef.current = key;
    keysRef.current = [...keysRef.current, key];
    onChange([...items, emptyScalarValue(dataType)]);
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <NoxusFormLabel label={label} noMargin />}
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">No items yet.</p>
      )}
      {items.map((item, index) => (
        <div key={keys[index]} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <ControlledScalarRow
              value={item}
              onChange={(next) => setItem(index, next)}
              dataType={dataType}
              isDisabled={isDisabled}
              autoFocus={keys[index] === pendingFocusKeyRef.current}
              disableVariables={disableVariables}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            disabled={isDisabled}
            onClick={() => removeItem(index)}
            aria-label="Remove item"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        className={cn(" gap-1.5")}
        disabled={isDisabled}
        onClick={addItem}
      >
        <Plus size={14} />
        Add item
      </Button>
    </div>
  );
}

/** Controlled counterpart of TypedValueField; the trigger mappers use it directly. */
export function TypedValueInput({
  dataType,
  isList,
  value,
  onChange,
  isDisabled,
  label,
  placeholder,
  fileVariant,
  disableVariables,
}: TypedValueInputProps) {
  if (isList) {
    return (
      <ListValueInput
        dataType={dataType}
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        label={label}
        fileVariant={fileVariant}
        disableVariables={disableVariables}
      />
    );
  }
  return (
    <ScalarValueInput
      dataType={dataType}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      label={label}
      placeholder={placeholder}
      fileVariant={fileVariant}
      disableVariables={disableVariables}
    />
  );
}

function ListValueField({
  control,
  name,
  label,
  dataType,
  isDisabled,
  fileVariant,
  disableVariables,
}: TypedValueFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <ListValueInput
          dataType={dataType}
          value={field.value}
          onChange={field.onChange}
          isDisabled={isDisabled}
          label={label}
          fileVariant={fileVariant}
          disableVariables={disableVariables}
        />
      )}
    />
  );
}

export function TypedValueField(props: TypedValueFieldProps) {
  const { setValue, name, dataType, isList } = props;
  const typeKey = `${dataType}:${isList}`;

  // Reset the value when the element type / list-ness changes, so a value left
  // over from the previous type (e.g. strings reinterpreted as booleans) is
  // never carried over. Skips the initial mount so a saved value is preserved.
  const prevTypeKey = useRef(typeKey);
  useEffect(() => {
    if (prevTypeKey.current === typeKey) return;
    prevTypeKey.current = typeKey;
    setValue?.(name, emptyTypedValue(dataType, isList));
  }, [typeKey, setValue, name, dataType, isList]);

  // Remount when the type changes so the editor never momentarily renders the
  // old value before the reset above lands.
  const remountKey = typeKey;
  if (props.isList) return <ListValueField key={remountKey} {...props} />;
  return (
    <ScalarValueField
      key={remountKey}
      control={props.control}
      name={props.name}
      label={props.label}
      dataType={props.dataType}
      isDisabled={props.isDisabled}
      fileVariant={props.fileVariant}
      disableVariables={props.disableVariables}
    />
  );
}
